import { flexRender } from '@tanstack/react-table'

import type { Column, RowData } from '@tanstack/react-table'

// Module augmentation: opt-in marker for columns that should pin their
// pixel width. Used by tables with few columns where the default
// proportional fill would over-stretch action or numeric columns.
//
// `fixedWidth` is *load-bearing in two places* — both must agree:
// 1. `<colgroup>` col `width: column.getSize()` (vs `'auto'`)
// 2. `getCommonPinningStyles` `width: column.getSize()` on `<th>`/`<td>`
//    (vs `undefined`, which lets the colgroup `'auto'` actually
//    auto-distribute under `table-layout: fixed`).
// Without (2), the browser treats every TH inline `width` as an explicit
// declaration and proportionally scales ALL columns to fill the table
// width — breaking the colgroup `'auto'` intent and inflating both
// "fixed" cols (40/72/120 px) and intended-auto cols past their target
// size. See [MEM-81] / TIK-153 RCA.
declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    fixedWidth?: boolean
  }
}

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import type { CSSProperties } from 'react'
import { useDataTable } from './DataTableProvider'
import { AlertCircle } from 'lucide-react'

const getCommonPinningStyles = (column: Column<any>): CSSProperties => {
  const isPinned = column.getIsPinned()
  const isLastLeftPinnedColumn = isPinned === 'left' && column.getIsLastColumn('left')
  const isFirstRightPinnedColumn = isPinned === 'right' && column.getIsFirstColumn('right')
  const meta = column.columnDef.meta as { fixedWidth?: boolean } | undefined
  // Emit explicit width ONLY for cols that need a fixed size — sticky
  // pinning (browser needs width to lay sticky cell) or opted-in
  // `meta.fixedWidth`. Non-pinned auto cols return `undefined` so the
  // colgroup `'auto'` width is honored and the browser truly distributes
  // remaining table width across them. See [MEM-81] / TIK-153 RCA.
  const hasExplicitWidth = !!isPinned || !!meta?.fixedWidth

  return {
    boxShadow: isLastLeftPinnedColumn
      ? '-2px 0 4px -2px gray inset'
      : isFirstRightPinnedColumn
        ? '3px 0 3px -3px gray inset'
        : undefined,
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    position: isPinned ? 'sticky' : 'relative',
    width: hasExplicitWidth ? column.getSize() : undefined,
    zIndex: isPinned ? 1 : 0,
  }
}

export const DataTableContent = <RecordType,>() => {
  const { table, isLoading, error, onRowClick } = useDataTable<RecordType>()
  const hasRows = table.getRowModel().rows?.length > 0

  // Don't show "No results" if we're loading and have no data yet
  // This prevents the flash of "No results" during pagination
  const showNoResults = !hasRows && !isLoading && !error
  const showError = error && !isLoading

  // Get all visible columns to create colgroup
  const visibleColumns = table.getVisibleLeafColumns()

  return (
    <div className="relative">
      <Table style={{ tableLayout: 'fixed' }}>
        <colgroup>
          {visibleColumns.map((column) => {
            const meta = column.columnDef.meta as
              | { fixedWidth?: boolean }
              | undefined
            // With table-layout: fixed + a table that fills its container,
            // an `auto` colgroup col lets the column absorb extra space
            // while sibling cols with explicit pixel widths stay pinned.
            // Default stays 'auto' for back-compat with existing tables;
            // opt in with meta.fixedWidth to pin a column to its size.
            const width =
              column.id === 'select'
                ? 40
                : meta?.fixedWidth
                  ? column.getSize()
                  : 'auto'
            return <col key={column.id} style={{ width }} />
          })}
        </colgroup>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const { column } = header
                const isSelectionColumn = column.id === 'select'
                return (
                  <TableHead
                    key={header.id}
                    style={{
                      ...getCommonPinningStyles(column),
                      ...(isSelectionColumn && { width: 40, minWidth: 40, maxWidth: 40 })
                    }}
                    className={isSelectionColumn ? 'px-0' : undefined}
                  >
                    {isSelectionColumn ? (
                      <div className="flex justify-center">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </div>
                    ) : (
                      header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {showError ? (
            <TableRow>
              <TableCell colSpan={table.getAllColumns().length} className="h-32">
                <div className="flex flex-col items-center justify-center gap-2 text-destructive">
                  <AlertCircle className="h-8 w-8" />
                  <p className="font-medium">Failed to load data</p>
                  <p className="text-sm text-muted-foreground max-w-md text-center">
                    {error.message || 'An unexpected error occurred'}
                  </p>
                </div>
              </TableCell>
            </TableRow>
          ) : hasRows ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-testid="data-table-row"
                data-row-id={(row.original as { id?: string })?.id}
                data-state={row.getIsSelected() && 'selected'}
                className={onRowClick ? 'cursor-pointer' : undefined}
                onClick={onRowClick ? (e) => {
                  // Don't trigger row click if clicking on selection cell
                  const target = e.target as HTMLElement
                  if (target.closest('[data-selection-cell="true"]')) {
                    return
                  }
                  onRowClick(row.original)
                } : undefined}
              >
                {row.getVisibleCells().map((cell) => {
                  const isSelectionCell = cell.column.id === 'select'
                  return (
                    <TableCell
                      key={cell.id}
                      style={{
                        ...getCommonPinningStyles(cell.column),
                        ...(isSelectionCell && { width: 40, minWidth: 40, maxWidth: 40 })
                      }}
                      data-selection-cell={isSelectionCell ? 'true' : undefined}
                      className={isSelectionCell ? 'px-0' : undefined}
                    >
                      {isSelectionCell ? (
                        <div className="flex justify-center">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </div>
                      ) : (
                        flexRender(cell.column.columnDef.cell, cell.getContext())
                      )}
                    </TableCell>
                  )
                })}
              </TableRow>
            ))
          ) : showNoResults ? (
            <TableRow>
              <TableCell colSpan={table.getAllColumns().length} className="h-24 text-center">
                No results.
              </TableCell>
            </TableRow>
          ) : (
            // Show empty body during loading to maintain table height
            <TableRow>
              <TableCell colSpan={table.getAllColumns().length} className="h-24">
                &nbsp;
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {isLoading && (
        <div
          data-testid="data-table-loading"
          className="absolute inset-x-0 bottom-0 flex items-center justify-center backdrop-blur-sm"
          style={{
            top: `${table.getHeaderGroups().length * 52 - 12}px`,
            background: 'linear-gradient(to bottom, transparent 0px, hsl(var(--background) / 0.5) 12px)',
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={24}
            height={24}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="animate-spin text-primary"
          >
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
        </div>
      )}
    </div>
  )
}
